import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Search, X, ArrowRight } from "lucide-react";
import Reveal from "@/components/Reveal";
import SectionHead from "@/components/SectionHead";
import { API } from "@/lib/api";
import { PANEL_FILTERS } from "@/data/content";
import { useSEO } from "@/lib/useSEO";

const initials = (name) => name.split(" ").filter(Boolean).slice(0, 2).map((w) => w[0]).join("");

export default function PanelPage() {
  useSEO("Advocate Panel | Smart Legal Solutions", "A multidisciplinary panel of advocates across litigation, advisory, documentation and dispute-resolution practice areas in Vijayawada.");
  const [advocates, setAdvocates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [area, setArea] = useState("All Advocates");
  const [language, setLanguage] = useState("");
  const [city, setCity] = useState("");
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    setLoading(true);
    const t = setTimeout(() => {
      API.get("/advocates", { params: { q: q || undefined, practice_area: area, language: language || undefined, city: city || undefined } })
        .then((r) => setAdvocates(r.data))
        .catch(() => setAdvocates([]))
        .finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(t);
  }, [q, area, language, city]);

  const languages = useMemo(() => [...new Set(advocates.flatMap((a) => a.languages || []))].sort(), [advocates]);
  const cities = useMemo(() => [...new Set(advocates.map((a) => a.city).filter(Boolean))].sort(), [advocates]);

  return (
    <div data-testid="advocate-panel-page" className="bg-navy pt-32">
      <div className="mx-auto max-w-7xl px-5 pb-24 lg:px-10">
        <Reveal>
          <p className="mono-label">A Multidisciplinary Legal Panel</p>
          <h1 className="mt-4 max-w-3xl font-serif text-4xl font-medium leading-[1.1] tracking-tight text-ivory sm:text-5xl lg:text-6xl">
            One Institution. Diverse Legal Capabilities.
          </h1>
          <div className="gold-hairline mt-8" />
          <p className="mt-8 max-w-2xl text-base leading-relaxed text-ivory/55">
            Different matters require different legal experience. Smart Legal Solutions follows a structured approach to
            identifying the appropriate advocate or legal team according to the nature, jurisdiction, urgency and
            complexity of each matter. Profiles are published only after verification and consent.
          </p>
        </Reveal>

        <Reveal delay={0.1} className="mt-12">
          <div className="grid gap-4 md:grid-cols-4">
            <div className="relative md:col-span-2">
              <Search size={15} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate" />
              <input
                data-testid="advocate-search-input"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search by name or enrolment reference…"
                className="field-input-dark !pl-11"
                aria-label="Search advocates"
              />
            </div>
            <select data-testid="advocate-filter-language-select" value={language} onChange={(e) => setLanguage(e.target.value)} className="field-input-dark" aria-label="Filter by language">
              <option value="">All Languages</option>
              {languages.map((l) => <option key={l} value={l}>{l}</option>)}
            </select>
            <select data-testid="advocate-filter-city-select" value={city} onChange={(e) => setCity(e.target.value)} className="field-input-dark" aria-label="Filter by city">
              <option value="">All Cities</option>
              {cities.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div className="mt-6 flex gap-2.5 overflow-x-auto pb-2" role="group" aria-label="Filter by practice area">
            {PANEL_FILTERS.map((f) => (
              <button
                key={f}
                data-testid={`advocate-filter-${f.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                onClick={() => setArea(f)}
                className={`shrink-0 border px-4 py-2 font-mono text-[10px] uppercase tracking-[0.16em] transition-colors duration-300 ${area === f ? "border-gold bg-gold text-navy" : "border-ivory/20 text-ivory/50 hover:border-gold/60 hover:text-ivory"}`}
              >
                {f}
              </button>
            ))}
          </div>
        </Reveal>

        <p className="mt-10 font-mono text-[10px] uppercase tracking-[0.25em] text-slate" data-testid="advocate-count">
          {loading ? "Loading…" : `${advocates.length} verified ${advocates.length === 1 ? "profile" : "profiles"}`}
        </p>

        <div className="mt-6 grid gap-px bg-ivory/10 sm:grid-cols-2 lg:grid-cols-3">
          {loading && Array.from({ length: 6 }).map((_, i) => <div key={i} className="h-44 animate-pulse bg-navy" />)}
          {!loading && advocates.length === 0 && (
            <div className="col-span-full bg-navy p-16 text-center" data-testid="advocate-empty-state">
              <p className="font-serif text-2xl text-ivory/60">No profiles match the current filters.</p>
            </div>
          )}
          {!loading &&
            advocates.map((a, i) => (
              <div key={a.id} className="bg-navy">
                <button
                  data-testid={`advocate-card-${i}`}
                  onClick={() => setSelected(a)}
                  className="group flex h-full w-full flex-col p-7 text-left transition-colors duration-500 hover:bg-oxford"
                >
                  <div className="flex items-center gap-5">
                    <div className="flex h-14 w-14 shrink-0 items-center justify-center border border-gold/40 transition-colors duration-500 group-hover:border-gold">
                      <span className="font-serif text-base tracking-[0.1em] text-gold">{initials(a.name)}</span>
                    </div>
                    <div>
                      <p className="font-serif text-xl font-medium text-ivory">{a.name}</p>
                      <p className="mt-0.5 font-mono text-[10px] uppercase tracking-[0.18em] text-ivory/40">{a.designation}</p>
                    </div>
                  </div>
                  <div className="mt-5 flex flex-wrap gap-1.5">
                    {(a.practice_areas || []).map((p) => (
                      <span key={p} className="border border-ivory/15 px-2.5 py-1 font-mono text-[9px] uppercase tracking-[0.12em] text-ivory/50">{p}</span>
                    ))}
                  </div>
                  <span className="mt-6 flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.22em] text-gold/0 transition-all duration-500 group-hover:text-gold">
                    View Professional Profile <ArrowRight size={11} />
                  </span>
                </button>
              </div>
            ))}
        </div>

        <Reveal className="mt-14">
          <p className="border-l-2 border-gold/50 pl-5 text-sm leading-relaxed text-ivory/45">
            Panel profiles are informational and published with consent. The institution does not rank advocates, publish
            success rates, or make comparative claims. Matter allocation follows the subject, jurisdiction and
            procedural requirements of each matter.
          </p>
        </Reveal>
      </div>

      <AnimatePresence>
        {selected && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setSelected(null)}
              className="fixed inset-0 z-50 bg-ink/80 backdrop-blur-sm"
              data-testid="advocate-drawer-overlay"
            />
            <motion.aside
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
              className="fixed inset-y-0 right-0 z-50 w-full max-w-lg overflow-y-auto border-l border-gold/25 bg-navy"
              data-testid="advocate-profile-drawer"
              role="dialog"
              aria-label={`Profile of ${selected.name}`}
            >
              <div className="p-8 sm:p-10">
                <div className="flex items-start justify-between">
                  <div className="flex h-20 w-20 items-center justify-center border border-gold/50">
                    <span className="font-serif text-2xl tracking-[0.1em] text-gold">{initials(selected.name)}</span>
                  </div>
                  <button data-testid="advocate-drawer-close-btn" onClick={() => setSelected(null)} aria-label="Close profile" className="border border-ivory/20 p-2 text-ivory/60 transition-colors hover:border-gold hover:text-gold">
                    <X size={16} />
                  </button>
                </div>
                <h2 className="mt-8 font-serif text-3xl font-medium text-ivory">{selected.name}</h2>
                <p className="mt-2 font-mono text-[11px] uppercase tracking-[0.22em] text-gold">{selected.designation}</p>

                <div className="mt-10 space-y-0 border-t border-ivory/10">
                  {[
                    ["Enrolment", selected.enrolment],
                    ["Year of Enrolment", selected.year_enrolled],
                    ["Qualifications", selected.qualifications],
                    ["Experience", selected.experience_years ? `${selected.experience_years} years (as recorded)` : ""],
                    ["City", selected.city],
                    ["Languages", (selected.languages || []).join(", ")],
                    ["Courts & Forums", (selected.courts || []).join(" · ")],
                  ].filter(([, v]) => v).map(([k, v]) => (
                    <div key={k} className="grid grid-cols-3 gap-4 border-b border-ivory/10 py-4">
                      <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-slate">{k}</span>
                      <span className="col-span-2 text-sm leading-relaxed text-ivory/75">{v}</span>
                    </div>
                  ))}
                </div>

                <div className="mt-8">
                  <p className="mono-label">Practice Areas</p>
                  <div className="mt-4 flex flex-wrap gap-2">
                    {(selected.practice_areas || []).map((p) => (
                      <span key={p} className="border border-gold/30 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.14em] text-ivory/70">{p}</span>
                    ))}
                  </div>
                </div>

                {selected.bio && <p className="mt-8 text-sm leading-relaxed text-ivory/55">{selected.bio}</p>}

                <Link
                  to="/contact"
                  state={{ area: (selected.practice_areas || [])[0] || "" }}
                  data-testid="advocate-drawer-enquire-btn"
                  className="btn-outline-gold mt-10 w-full justify-center"
                >
                  Submit Matter Details <ArrowRight size={13} />
                </Link>
                <p className="mt-6 font-mono text-[10px] leading-relaxed tracking-[0.06em] text-slate">
                  Profile published with consent after internal verification. Enquiries are subject to conflict checks
                  and availability.
                </p>
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}
